import React, { useEffect, useState } from 'react';
import { Loader2, Save, Mail } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';

const inputCls =
  'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children }) => (
  <div>
    <label dir={dir} className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
      {label}
    </label>
    {children}
  </div>
);

const AdminContactPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [d, setD] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let alive = true;
    adminApi.get('/settings/contact_page').then(({ data }) => {
      if (!alive) return;
      setD(data?.value || {});
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const setKey = (k, v) => setD((prev) => ({ ...prev, [k]: v }));
  const setBil = (k, sub, v) => setD((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), [sub]: v } }));
  const setSocial = (k, v) => setD((prev) => ({ ...prev, social: { ...(prev.social || {}), [k]: v } }));

  const onSave = async () => {
    setSaving(true); setError(''); setMsg('');
    try {
      await adminApi.put('/settings/contact_page', { value: d });
      setMsg(isAr ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  const heroTitle    = d.hero_title    || {};
  const heroSubtitle = d.hero_subtitle || {};
  const address      = d.address       || {};
  const social       = d.social        || {};

  return (
    <div className="space-y-6" data-testid="admin-contact-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'صفحة التواصل' : 'Contact Us Page'}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isAr ? 'حرّر نصوص وقسم البطل ومعلومات الاتصال وروابط التواصل الاجتماعي. اترك أي حقل فارغاً لاستخدام النص الافتراضي.'
                : 'Edit the hero copy, contact info, and social links. Leave any field blank to fall back to the built-in default.'}
        </p>
        <p className={`mt-2 text-xs ${isAr ? 'text-right' : 'text-left'} text-amber-600 dark:text-amber-400`}>
          {isAr
            ? 'ملاحظة: بيانات SMTP لإرسال البريد تُدار من متغيرات البيئة على الخادم ولا يمكن تعديلها من هنا.'
            : 'Note: SMTP credentials for email delivery are managed via server environment variables and can\'t be edited here.'}
        </p>
      </div>

      {error && <div className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">{error}</div>}
      {msg   && <div className="px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">{msg}</div>}

      {/* Hero */}
      <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{isAr ? 'قسم البطل' : 'Hero section'}</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={isAr ? 'العنوان (عربي)' : 'Title (Arabic)'} dir="rtl">
            <input dir="rtl" className={inputCls} value={heroTitle.ar || ''} onChange={(e) => setBil('hero_title', 'ar', e.target.value)} data-testid="ap-title-ar" />
          </Field>
          <Field label={isAr ? 'العنوان (إنجليزي)' : 'Title (English)'} dir="ltr">
            <input dir="ltr" className={inputCls} value={heroTitle.en || ''} onChange={(e) => setBil('hero_title', 'en', e.target.value)} data-testid="ap-title-en" />
          </Field>
          <Field label={isAr ? 'العنوان الفرعي (عربي)' : 'Subtitle (Arabic)'} dir="rtl">
            <textarea rows={3} dir="rtl" className={inputCls} value={heroSubtitle.ar || ''} onChange={(e) => setBil('hero_subtitle', 'ar', e.target.value)} data-testid="ap-subtitle-ar" />
          </Field>
          <Field label={isAr ? 'العنوان الفرعي (إنجليزي)' : 'Subtitle (English)'} dir="ltr">
            <textarea rows={3} dir="ltr" className={inputCls} value={heroSubtitle.en || ''} onChange={(e) => setBil('hero_subtitle', 'en', e.target.value)} data-testid="ap-subtitle-en" />
          </Field>
        </div>
      </section>

      {/* Contact info */}
      <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{isAr ? 'معلومات التواصل' : 'Contact information'}</h2>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={isAr ? 'الهاتف' : 'Phone'} dir="ltr">
            <input dir="ltr" className={inputCls} value={d.phone || ''} onChange={(e) => setKey('phone', e.target.value)} data-testid="ap-phone" />
          </Field>
          <Field label={isAr ? 'البريد الإلكتروني (للعرض)' : 'Display email'} dir="ltr">
            <input dir="ltr" className={inputCls} value={d.email || ''} onChange={(e) => setKey('email', e.target.value)} data-testid="ap-email" />
          </Field>
          <Field label={isAr ? 'العنوان (عربي)' : 'Address (Arabic)'} dir="rtl">
            <textarea rows={3} dir="rtl" className={inputCls} value={address.ar || ''} onChange={(e) => setBil('address', 'ar', e.target.value)} data-testid="ap-address-ar" />
          </Field>
          <Field label={isAr ? 'العنوان (إنجليزي)' : 'Address (English)'} dir="ltr">
            <textarea rows={3} dir="ltr" className={inputCls} value={address.en || ''} onChange={(e) => setBil('address', 'en', e.target.value)} data-testid="ap-address-en" />
          </Field>
          <div className="md:col-span-2">
            <Field label={isAr ? 'رابط خرائط Google' : 'Google Maps URL'} dir="ltr">
              <input dir="ltr" className={inputCls} value={d.map_url || ''} onChange={(e) => setKey('map_url', e.target.value)} data-testid="ap-map-url" />
            </Field>
          </div>
        </div>
      </section>

      {/* Social */}
      <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{isAr ? 'روابط التواصل الاجتماعي' : 'Social media links'}</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {['instagram', 'linkedin', 'tiktok', 'x'].map((k) => (
            <Field key={k} label={k[0].toUpperCase() + k.slice(1)} dir="ltr">
              <input dir="ltr" className={inputCls} value={social[k] || ''} onChange={(e) => setSocial(k, e.target.value)} data-testid={`ap-social-${k}`} />
            </Field>
          ))}
        </div>
      </section>

      <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
        <button type="button" data-testid="admin-contact-save" onClick={onSave} disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isAr ? 'حفظ' : 'Save changes'}
        </button>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
        <Mail className="w-3.5 h-3.5" />
        {isAr ? 'رسائل النموذج تُرسل إلى:' : 'Form submissions are delivered to:'} <b>support@ebe.com.sa</b>
      </div>
    </div>
  );
};

export default AdminContactPage;
